import type { CodeableConcept, HumanName, Resource } from '@medplum/fhirtypes';

/**
 * Semantic accessors: cross-type answers to "what is this resource's name /
 * date". Mixed-type surfaces (tables, chips, search results) need one
 * consistent projection per concept; the per-type dispatch lives here once.
 */

export function formatHumanName(name: HumanName | undefined): string {
  if (!name) return '';
  return (
    name.text ??
    [name.prefix?.join(' '), name.given?.join(' '), name.family]
      .filter(Boolean)
      .join(' ')
  );
}

/** CodeableConcept display: explicit text wins, else the first coding's display. */
export function conceptText(
  concept: CodeableConcept | undefined,
): string | undefined {
  return concept?.text ?? concept?.coding?.[0]?.display;
}

function firstHumanName(
  resource: Resource & { name?: HumanName[] },
): string | undefined {
  const assembled = formatHumanName(resource.name?.[0]);
  return assembled || undefined;
}

/** Display name for any resource: human name, org/team name, or coded concept text. */
export function resourceDisplayName(resource: Resource): string {
  switch (resource.resourceType) {
    case 'Patient':
    case 'Practitioner':
    case 'RelatedPerson':
    case 'Person':
      return firstHumanName(resource) ?? resource.resourceType;
    case 'Organization':
    case 'CareTeam':
    case 'Location':
      return resource.name ?? resource.resourceType;
    case 'PractitionerRole':
      return (
        resource.code?.[0]?.text ??
        resource.specialty?.[0]?.text ??
        resource.resourceType
      );
    case 'Observation':
    case 'Condition':
    case 'DiagnosticReport':
    case 'Procedure':
    case 'AllergyIntolerance':
    case 'Immunization':
    case 'Medication':
      return (
        ('code' in resource ? conceptText(resource.code) : undefined) ??
        ('vaccineCode' in resource
          ? conceptText(resource.vaccineCode)
          : undefined) ??
        resource.resourceType
      );
    case 'MedicationRequest':
    case 'MedicationStatement':
      return (
        conceptText(resource.medicationCodeableConcept) ??
        resource.medicationReference?.display ??
        resource.resourceType
      );
    case 'DocumentReference':
      return (
        resource.description ?? resource.type?.text ?? resource.resourceType
      );
    default:
      return resource.resourceType;
  }
}

/** Clinically-relevant date for any resource, ISO string. Falls back to meta.lastUpdated. */
export function resourceDate(resource: Resource): string | undefined {
  switch (resource.resourceType) {
    case 'Observation':
    case 'DiagnosticReport':
      return resource.effectiveDateTime ?? resource.issued;
    case 'Condition':
      return resource.onsetDateTime ?? resource.recordedDate;
    case 'Procedure':
      return resource.performedDateTime ?? resource.performedPeriod?.start;
    case 'AllergyIntolerance':
      return resource.onsetDateTime ?? resource.recordedDate;
    case 'MedicationRequest':
      return resource.authoredOn;
    case 'MedicationStatement':
      return resource.effectiveDateTime ?? resource.dateAsserted;
    case 'Immunization':
      return resource.occurrenceDateTime ?? resource.recorded;
    case 'DocumentReference':
      return resource.date;
    case 'Encounter':
      return resource.period?.start;
    default:
      return resource.meta?.lastUpdated;
  }
}
